// ── Candidate Gate ─────────────────────────────────────────────
// Pure TypeScript — no platform dependencies.

import type { PropertyData, BuyerCriteria, CandidateResult } from './types.ts';
import { normalizeCityName, normalizeCityArray } from './cities.ts';

const ROOMS_MAX_DIFF = 2;

export function evaluateCandidate(
  property: PropertyData,
  criteria: BuyerCriteria,
): CandidateResult {
  if (!property) {
    return { isCandidate: false, reason: 'חסרים נתוני נכס' };
  }

  // 1. City
  if (criteria.cities && criteria.cities.length > 0) {
    const propCity = normalizeCityName(property.city);
    const criteriaCities = normalizeCityArray(criteria.cities);
    if (propCity && !criteriaCities.includes(propCity)) {
      return { isCandidate: false, reason: 'מחוץ לערים שנבחרו' };
    }
  }

  // 2. Property Type
  if (criteria.propertyTypes && criteria.propertyTypes.length > 0) {
    const propType = property.propertyType;
    if (propType && !criteria.propertyTypes.includes(propType)) {
      return { isCandidate: false, reason: 'סוג הנכס אינו תואם' };
    }
  }

  // 3. Rooms (±2 max difference)
  if (criteria.rooms && criteria.rooms.length > 0 && property.rooms !== undefined && property.rooms !== null) {
    const propRooms = property.rooms;
    const minDiff = Math.min(...criteria.rooms.map(r => Math.abs(r - propRooms)));
    if (minDiff > ROOMS_MAX_DIFF) {
      return { isCandidate: false, reason: 'הפרש חדרים גדול מדי' };
    }
  }

  // 4. Budget — reject only when completely unrealistic
  if (criteria.priceMin && criteria.priceMax && property.price !== undefined && property.price !== null) {
    const price = property.price;
    const min = criteria.priceMin;
    const max = criteria.priceMax;
    const range = max - min;
    if (price > max * 1.5) {
      return { isCandidate: false, reason: 'מחיר גבוה משמעותית מהתקציב' };
    }
    if (price < min * 0.5) {
      return { isCandidate: false, reason: 'מחיר נמוך משמעותית מהתקציב' };
    }
    if (range > 0 && price > max + range * 3) {
      return { isCandidate: false, reason: 'מחיר גבוה משמעותית מהתקציב' };
    }
  } else if (criteria.priceMax && property.price !== undefined && property.price !== null) {
    if (property.price > criteria.priceMax * 1.5) {
      return { isCandidate: false, reason: 'מחיר גבוה משמעותית מהתקציב' };
    }
  } else if (criteria.priceMin && property.price !== undefined && property.price !== null) {
    if (property.price < criteria.priceMin * 0.5) {
      return { isCandidate: false, reason: 'מחיר נמוך משמעותית מהתקציב' };
    }
  }

  // 5. Mandatory Features — reject only when definitely missing (not unknown)
  if (criteria.parkingRequired && property.hasParking === false) {
    return { isCandidate: false, reason: 'חסר חניה (חובה)' };
  }
  if (criteria.elevatorRequired && property.hasElevator === false) {
    return { isCandidate: false, reason: 'חסר מעלית (חובה)' };
  }
  if (criteria.saferoomRequired && property.hasSaferoom === false) {
    return { isCandidate: false, reason: 'חסר ממ"ד (חובה)' };
  }

  return { isCandidate: true };
}
